import { useQuery } from 'react-query'
import { useNavigate } from 'react-router'

import { buildProjectCatalogPath } from 'features/catalog/utils'
import { CUSTOM_DASHBOARD_PREVIEWS_QUERY_KEY } from 'features/dashboards-v2/constants'
import { dashboards2DetailPath } from 'features/dashboards-v2/paths'
import type { DashboardV2Service } from 'features/dashboards-v2/service'
import type { FilterUIState } from 'features/filters/store'
import { useFilterUIStore } from 'features/filters/store'
import type { FilterOptionSelect } from 'features/filters/types'
import { buildEntityCommands } from 'features/project/features/command-palette/build-entity-commands'
import type { PaletteCommand } from 'features/project/features/command-palette/types'
import { useFeatureFlags } from 'features/project/hooks/use-feature-flags'
import { useProjectStore } from 'features/project/store'
import { SAVED_VIEWS_QUERY_KEY } from 'features/saved-views/constants'
import type { SavedViewService } from 'features/saved-views/service'
import type { SavedView } from 'features/saved-views/types'
import type { ProjectState } from 'types/project'

const EMPTY_CATALOG = { brands: [], products: [], themes: [], sources: [] }

const optionValues = (
  defaultFilterList: FilterUIState['defaultFilterList'],
  field: string,
): string[] => {
  const option = defaultFilterList.find((el) => el.field === field)
  if (!option || !('values' in option)) return []
  return (option as FilterOptionSelect).values.map((value) => value.value)
}

// One command per dashboard / saved view / catalog entity. The two queries
// share their keys (and so their cache entries) with the owning features;
// `enabled` gates fetching to while the palette is open. Catalog names come
// from the already-loaded default filter list — no fetch.
export const useEntityCommands = ({
  dashboardService,
  savedViewService,
  applySavedView,
  enabled,
}: {
  dashboardService: DashboardV2Service
  savedViewService: SavedViewService
  applySavedView: (view: SavedView) => void
  enabled: boolean
}): PaletteCommand[] => {
  const navigate = useNavigate()
  const projectId = useProjectStore((state: ProjectState) => state.projectId)
  const details = useProjectStore((state: ProjectState) => state.details)
  const { data: featureFlags } = useFeatureFlags()
  const defaultFilterList = useFilterUIStore(
    (state: FilterUIState) => state.defaultFilterList,
  )

  const canSeeDashboards = !!(
    featureFlags as Record<string, boolean> | undefined
  )?.['enable_charts_2']

  const { data: dashboardPreviews } = useQuery(
    [CUSTOM_DASHBOARD_PREVIEWS_QUERY_KEY, projectId, false],
    () => dashboardService.getDashboardPreviews(projectId, false),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled: enabled && !!projectId && canSeeDashboards,
    },
  )

  const { data: savedViews } = useQuery(
    [SAVED_VIEWS_QUERY_KEY, projectId],
    () => savedViewService.getSavedViews(projectId),
    { refetchOnWindowFocus: false, enabled: enabled && !!projectId },
  )

  if (!enabled) return []

  const catalogHidden =
    !!details?.clientSettings?.project?.pages?.catalog?.hidden

  return buildEntityCommands({
    dashboards: canSeeDashboards
      ? (dashboardPreviews ?? []).map((preview) => ({
          uuid: preview.dashboard.uuid,
          name: preview.dashboard.name,
        }))
      : [],
    savedViews: savedViews ?? [],
    catalog: catalogHidden
      ? EMPTY_CATALOG
      : {
          brands: optionValues(defaultFilterList, 'brand'),
          products: optionValues(defaultFilterList, 'product_hierarchy'),
          themes: optionValues(defaultFilterList, 'theme'),
          sources: optionValues(defaultFilterList, 'source_type'),
        },
    openDashboard: (uuid) => navigate(dashboards2DetailPath(projectId, uuid)),
    applySavedView,
    openCatalogItem: (collection, name) =>
      navigate(buildProjectCatalogPath(projectId, collection, name)),
  })
}
